// components/reports/CustomerReport.tsx
import React, { useState } from "react";
import {
  Card,
  Col,
  Row,
  Statistic,
  Table,
  Spin,
  Alert,
  Space,
  Typography,
  Button,
  Flex,
  message,
} from "antd";
import {
  UserOutlined,
  DollarOutlined,
  RiseOutlined,
  TrophyOutlined,
  FileExcelOutlined,
} from "@ant-design/icons";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { useQuery } from "react-query";
import {
  customerReportService,
  type ReportParams,
} from "../../services/customerReportService";
import { exportCustomerListReportToExcel } from "../../utils/excelExport";

const { Text } = Typography;

interface CustomerReportProps {
  params: ReportParams;
}

const COLORS = ["#667eea", "#f093fb", "#4facfe", "#43e97b"];

const CustomerReport: React.FC<CustomerReportProps> = ({ params }) => {
  const [exportLoading, setExportLoading] = useState(false);

  const { data, isLoading, error } = useQuery(
    ["customer-report", params],
    () => customerReportService.getCustomerReport(params),
    {
      enabled: !!params.startDate && !!params.endDate,
      staleTime: 5 * 60 * 1000,
    }
  );

  if (isLoading) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="ເກີດຂໍ້ຜິດພາດ"
        description="ບໍ່ສາມາດດຶງຂໍ້ມູນລາຍງານລູກຄ້າໄດ້"
        type="error"
        showIcon
      />
    );
  }

  const reportData = data?.data;

  if (!reportData) {
    return (
      <Alert
        message="ບໍ່ມີຂໍ້ມູນ"
        description="ບໍ່ພົບຂໍ້ມູນລາຍງານໃນຊ່ວງເວລາທີ່ເລືອກ"
        type="info"
        showIcon
      />
    );
  }

  // เตรียมข้อมูลสำหรับกราฟ
  const genderChartData =
    reportData &&
    reportData?.genderDistribution?.map((item: any) => ({
      name: item.gender,
      value: item.count,
    }));

  const dailyChartData = reportData?.dailyStats?.map((item: any) => ({
    date: item._id,
    customers: item.newCustomers,
  }));

  // Columns สำหรับ Top Spenders
  const topSpendersColumns = [
    {
      title: "ລຳດັບ",
      key: "rank",
      width: 80,
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: "ລະຫັດລູກຄ້າ",
      dataIndex: "customerId",
      key: "customerId",
    },
    {
      title: "ຊື່ລູກຄ້າ",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "ເບີໂທ",
      dataIndex: "phone",
      key: "phone",
    },
    {
      title: "ຍອດໃຊ້ຈ່າຍ",
      dataIndex: "totalSpent",
      key: "totalSpent",
      render: (value: number) => (
        <Text strong style={{ color: "#52c41a" }}>
          {new Intl.NumberFormat("lo-LA").format(value)} ₭
        </Text>
      ),
      sorter: (a: any, b: any) => a.totalSpent - b.totalSpent,
    },
  ];

  // ฟังก์ชัน Export
  const handleExportToExcel = async () => {
    setExportLoading(true);
    try {
      const dateRangeInfo = {
        startDate: params.startDate,
        endDate: params.endDate,
      };

      // ✅ แก้ไขตรงนี้
      if (reportData.topSpenders) {
        exportCustomerListReportToExcel(reportData.topSpenders, dateRangeInfo);
        message.success("ສົ່ງອອກລາຍງານຄອສສຳເລັດ");
      } else {
        message.error("ບໍ່ມີຂໍ້ມູນສຳລັບສົ່ງອອກ");
      }
    } catch (error) {
      console.error("Export error:", error);
      message.error("ເກີດຂໍ້ຜິດພາດໃນການສົ່ງອອກ");
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      {/* Statistics Cards */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={4}>
          <Card
            bordered={false}
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              borderRadius: 12,
            }}
          >
            <Statistic
              title={
                <Text style={{ color: "rgba(255,255,255,0.85)" }}>
                  ລູກຄ້າທັງໝົດ
                </Text>
              }
              value={reportData?.summary?.totalCustomers}
              suffix="ຄົນ"
              valueStyle={{ color: "#fff", fontSize: 24 }}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={4}>
          <Card
            bordered={false}
            style={{
              background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
              borderRadius: 12,
            }}
          >
            <Statistic
              title={
                <Text style={{ color: "rgba(255,255,255,0.85)" }}>
                  ລູກຄ້າໃໝ່
                </Text>
              }
              value={reportData?.summary?.newCustomersCount}
              suffix="ຄົນ"
              valueStyle={{ color: "#fff", fontSize: 24 }}
              prefix={<RiseOutlined />}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={4}>
          <Card
            bordered={false}
            style={{
              background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
              borderRadius: 12,
            }}
          >
            <Statistic
              title={
                <Text style={{ color: "rgba(255,255,255,0.85)" }}>
                  ລູກຄ້າ Active
                </Text>
              }
              value={reportData?.summary?.activeCustomersCount}
              suffix="ຄົນ"
              valueStyle={{ color: "#fff", fontSize: 24 }}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card
            bordered={false}
            style={{
              background: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
              borderRadius: 12,
            }}
          >
            <Statistic
              title={
                <Text style={{ color: "rgba(255,255,255,0.85)" }}>
                  ລາຍຮັບທັງໝົດກີບ
                </Text>
              }
              value={reportData?.summary?.totalRevenueLAK}
              suffix="₭"
              valueStyle={{ color: "#fff", fontSize: 24 }}
              prefix={<DollarOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card
            bordered={false}
            style={{
              background: "linear-gradient(135deg, #e9a443ff 0%, #f97538ff 100%)",
              borderRadius: 12,
            }}
          >
            <Statistic
              title={
                <Text style={{ color: "rgba(255,255,255,0.85)" }}>
                  ລາຍຮັບທັງໝົດບາດ
                </Text>
              }
              value={reportData?.summary?.totalRevenueTHB}
              suffix="₭"
              valueStyle={{ color: "#fff", fontSize: 24 }}
              prefix={<DollarOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Charts */}
      <Row gutter={[16, 16]}>
        {/* Gender Distribution */}
        <Col xs={24} lg={12}>
          <Card
            title={
              <Space>
                <UserOutlined style={{ color: "#667eea" }} />
                <Text strong>ສັດສ່ວນລູກຄ້າຕາມເພດ</Text>
              </Space>
            }
            bordered={false}
            style={{
              borderRadius: 12,
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            }}
          >
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={genderChartData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent = 0 }) =>
                    `${name}: ${(percent * 100).toFixed(0)}%`
                  }
                  outerRadius={90}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {genderChartData?.map((_: any, index: number) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </Col>

        {/* Daily Stats */}
        <Col xs={24} lg={12}>
          <Card
            title={
              <Space>
                <RiseOutlined style={{ color: "#667eea" }} />
                <Text strong>ລູກຄ້າໃໝ່ແຕ່ລະວັນ</Text>
              </Space>
            }
            bordered={false}
            style={{
              borderRadius: 12,
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            }}
          >
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={dailyChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" stroke="#8c8c8c" />
                <YAxis stroke="#8c8c8c" />
                <Tooltip />
                <Bar dataKey="customers" fill="#667eea" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>

      {/* Top Spenders Table */}
      <Card
        title={
          <Flex justify="space-between" align="center">
            <Space>
              <TrophyOutlined style={{ color: "#667eea" }} />
              <Text strong>ລູກຄ້າທີ່ໃຊ້ຈ່າຍສູງສຸດ</Text>
            </Space>

            <Button
              type="primary"
              icon={<FileExcelOutlined />}
              onClick={handleExportToExcel}
              loading={exportLoading}
              style={{
                background: "linear-gradient(135deg, #52c41a 0%, #389e0d 100%)",
                border: "none",
              }}
            >
              ສົ່ງອອກ Excel
            </Button>
          </Flex>
        }
        bordered={false}
        style={{ borderRadius: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}
      >
        <Table
          dataSource={reportData.topSpenders}
          columns={topSpendersColumns}
          pagination={false}
          rowKey="_id"
        />
      </Card>
    </Space>
  );
};

export default CustomerReport;
